import React, { useEffect, useState, useCallback } from 'react';
import { getServerSessionId } from '../../lib/serverSession';
import { svgCertificateGenerator } from '../../lib/svgCertificateGenerator';
import { sanitizeSvg } from '../../lib/sanitizeHtml';

interface UserLayoutOverride {
  id?: string;
  user_id: number;
  element_id: string;
  x_position?: number | null;
  y_position?: number | null;
  width?: number | null;
  height?: number | null;
  font_size?: number | null;
}

interface UserCertificateLayoutEditorProps {
  userId: number;
  onLayoutSaved?: () => void;
}

// NOTE: We deliberately scope this editor to address-related elements only to
// avoid accidentally breaking global template positioning. The addressing
// fields are typically the ones that need per-user tweaks.
const ADDRESS_ELEMENT_FILTER = (elementId: string) =>
  elementId.toLowerCase().includes('address');

export const UserCertificateLayoutEditor: React.FC<UserCertificateLayoutEditorProps> = ({
  userId,
  onLayoutSaved
}) => {
  const [overrides, setOverrides] = useState<UserLayoutOverride[]>([]);
  const [availableElements, setAvailableElements] = useState<string[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSvg, setPreviewSvg] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(0.5); // 50% default zoom

  // Name text editing state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Address text editing state
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressStatus, setAddressStatus] = useState<string | null>(null);

  useEffect(() => {
    loadInitialData();
  }, [userId]);

  // Generate preview function
  const generatePreview = useCallback(async () => {
    if (!userId) return;

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const svg = await svgCertificateGenerator.generateCertificatePreviewForUser(userId);
      if (svg) {
        setPreviewSvg(svg);
      } else {
        setPreviewError('Failed to generate certificate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setPreviewLoading(false);
    }
  }, [userId]);

  // Load preview when panel opens
  useEffect(() => {
    if (showPreview && !previewSvg && !previewLoading) {
      generatePreview();
    }
  }, [showPreview, previewSvg, previewLoading, generatePreview]);

  const loadInitialData = async () => {
    if (!userId) return;
    setLoading(true);
    setStatusMessage(null);

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_user_layout_data', data: { userId } })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      // 1) Process template elements
      const addressElements = (result.templateElements || [])
        .map((el: any) => el.element_id as string)
        .filter((id: string) => !!id && ADDRESS_ELEMENT_FILTER(id));

      setAvailableElements(addressElements);

      // 2) Process existing overrides
      const cleanedOverrides: UserLayoutOverride[] = (result.overrides || []).map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        element_id: row.element_id,
        x_position: row.x_position,
        y_position: row.y_position,
        width: row.width,
        height: row.height,
        font_size: row.font_size
      }));

      setOverrides(cleanedOverrides);

      if (!selectedElementId) {
        const firstId = cleanedOverrides[0]?.element_id || addressElements[0] || null;
        setSelectedElementId(firstId || null);
      }

      // 3) Process address override and KYC fallback
      const addr = result.addressOverride;
      const kyc = result.kyc;

      if (addr) {
        // Load name from override or fall back to KYC
        setFirstName(addr.first_name || kyc?.first_name || '');
        setLastName(addr.last_name || kyc?.last_name || '');
        // Load address from override
        setAddressLine1(addr.address_line1 || '');
        setAddressLine2(addr.address_line2 || '');
        setCity(addr.city || '');
        setCountry(addr.country || '');
        setPostalCode(addr.postal_code || '');
      } else if (kyc) {
        // Fall back to KYC data for both name and address
        setFirstName(kyc.first_name || '');
        setLastName(kyc.last_name || '');
        setAddressLine1(kyc.street_address || '');
        setAddressLine2(`${kyc.city || ''}, ${kyc.country_name || ''}, ${kyc.postal_code || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ','));
        setCity(kyc.city || '');
        setCountry(kyc.country_name || '');
        setPostalCode(kyc.postal_code || '');
      }
    } catch (error) {
      console.error('Unexpected error loading user layout editor data:', error);
      setStatusMessage('Failed to load layout overrides for this user. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentOverride = (): UserLayoutOverride | null => {
    if (!selectedElementId) return null;
    const existing = overrides.find((o) => o.element_id === selectedElementId);
    if (existing) return existing;
    return {
      user_id: userId,
      element_id: selectedElementId,
      x_position: null,
      y_position: null,
      width: null,
      height: null,
      font_size: null
    };
  };

  const updateOverrideLocal = (updates: Partial<UserLayoutOverride>) => {
    const current = getCurrentOverride();
    if (!current) return;
    const merged = { ...current, ...updates } as UserLayoutOverride;

    setOverrides((prev) => {
      const withoutCurrent = prev.filter((o) => o.element_id !== merged.element_id);
      return [...withoutCurrent, merged];
    });
  };

  const adjustNumericField = (
    field: keyof Pick<UserLayoutOverride, 'x_position' | 'y_position' | 'width' | 'height' | 'font_size'>,
    delta: number,
    min: number,
    max: number
  ) => {
    const current = getCurrentOverride();
    if (!current) return;

    const raw = current[field] ?? 0;
    const next = Math.min(max, Math.max(min, (raw as number) + delta));
    updateOverrideLocal({ [field]: next } as Partial<UserLayoutOverride>);
  };

  const saveOverrides = async () => {
    const current = getCurrentOverride();
    if (!current) return;

    try {
      setSaving(true);
      setStatusMessage(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_layout_override',
          data: {
            userId,
            elementId: current.element_id,
            xPosition: current.x_position,
            yPosition: current.y_position,
            width: current.width,
            height: current.height,
            fontSize: current.font_size
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error saving user layout override:', result.error);
        setStatusMessage('❌ Failed to save layout override.');
        return;
      }

      setStatusMessage('✅ Layout override saved for this user.');
      // Refresh preview after saving
      if (showPreview) {
        setPreviewSvg(null);
        generatePreview();
      }
      if (onLayoutSaved) onLayoutSaved();
    } catch (error) {
      console.error('Unexpected error saving user layout override:', error);
      setStatusMessage('❌ Unexpected error while saving override.');
    } finally {
      setSaving(false);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  const resetOverridesForUser = async () => {
    if (!userId) return;

    try {
      setSaving(true);
      setStatusMessage(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'reset_layout_overrides', data: { userId } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error resetting user layout overrides:', result.error);
        setStatusMessage('❌ Failed to reset layout for this user.');
        return;
      }

      setOverrides([]);
      setStatusMessage('✅ Layout reset to template defaults for this user.');
      // Refresh preview after reset
      if (showPreview) {
        setPreviewSvg(null);
        generatePreview();
      }
      if (onLayoutSaved) onLayoutSaved();
    } catch (error) {
      console.error('Unexpected error resetting user layout overrides:', error);
      setStatusMessage('❌ Unexpected error while resetting layout.');
    } finally {
      setSaving(false);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  const saveAddressText = async () => {
    try {
      setSavingAddress(true);
      setAddressStatus(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_user_address_override',
          data: {
            userId,
            firstName: firstName.trim() || null,
            lastName: lastName.trim() || null,
            addressLine1: addressLine1.trim() || null,
            addressLine2: addressLine2.trim() || null,
            city: city.trim() || null,
            country: country.trim() || null,
            postalCode: postalCode.trim() || null
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error saving name/address override:', result.error);
        setAddressStatus('❌ Failed to save');
        return;
      }

      setAddressStatus('✅ Name & Address saved successfully');

      // Refresh preview after saving
      if (showPreview) {
        setPreviewSvg(null);
        generatePreview();
      }

      setTimeout(() => setAddressStatus(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setAddressStatus('❌ Error saving');
    } finally {
      setSavingAddress(false);
    }
  };

  const current = getCurrentOverride();

  if (loading && !current) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"></div>
        <span className="ml-3 text-gray-300 text-sm">Loading user layout overrides...</span>
      </div>
    );
  }

  if (!availableElements.length) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 text-sm text-gray-300">
        <p className="font-semibold mb-1">No address elements found in the template.</p>
        <p className="text-white">
          This editor is currently limited to elements with IDs containing "address". Configure address
          elements in the main template editor first, then return here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Preview Toggle Button */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
            showPreview
              ? 'bg-purple-600 text-white hover:bg-purple-500'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          {showPreview ? '📄 Hide Preview' : '👁️ Show Certificate Preview'}
        </button>
        {showPreview && (
          <button
            type="button"
            onClick={() => {
              setPreviewSvg(null);
              generatePreview();
            }}
            disabled={previewLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 text-sm font-semibold"
          >
            {previewLoading ? '🔄 Loading...' : '🔄 Refresh'}
          </button>
        )}
      </div>

      {/* Certificate Preview Panel */}
      {showPreview && (
        <div className="bg-gray-900 border border-gray-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              📜 Certificate Preview
              <span className="text-sm font-normal text-white">(Shows how certificate will look with current layout)</span>
            </h4>

            {/* Zoom Controls */}
            {previewSvg && !previewLoading && (
              <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-2 py-1">
                <button
                  type="button"
                  onClick={() => setPreviewZoom(z => Math.max(0.25, z - 0.25))}
                  disabled={previewZoom <= 0.25}
                  className="w-7 h-7 flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                >
                  −
                </button>
                <span className="text-sm text-gray-300 min-w-[50px] text-center font-mono">
                  {Math.round(previewZoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setPreviewZoom(z => Math.min(1.5, z + 0.25))}
                  disabled={previewZoom >= 1.5}
                  className="w-7 h-7 flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewZoom(0.5)}
                  className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-sm"
                >
                  Reset
                </button>
              </div>
            )}
          </div>

          {previewLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
              <span className="ml-3 text-gray-300">Generating preview...</span>
            </div>
          )}

          {previewError && (
            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-400 text-sm">
              ❌ {previewError}
            </div>
          )}

          {previewSvg && !previewLoading && (
            <div className="relative">
              <div
                className="w-full overflow-auto bg-white rounded-lg"
                style={{ maxHeight: '600px' }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizeSvg(previewSvg) }}
                  className="w-full"
                  style={{
                    minWidth: `${800 * previewZoom}px`,
                    transform: `scale(${previewZoom})`,
                    transformOrigin: 'top left',
                    width: `${100 / previewZoom}%`
                  }}
                />
              </div>
              <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded text-sm font-bold">
                PREVIEW ONLY
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-300 mb-1">Address Element</label>
        <select
          value={selectedElementId || ''}
          onChange={(e) => setSelectedElementId(e.target.value || null)}
          className="w-full px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-yellow-500"
        >
          {availableElements.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      </div>

      {current && (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-900 border border-gray-700 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">X Position</span>
                <span className="font-mono text-white">{current.x_position ?? 'auto'}px</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('x_position', -2, 0, 2000)}
                >
                  ← -2
                </button>
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('x_position', 2, 0, 2000)}
                >
                  +2 →
                </button>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Y Position</span>
                <span className="font-mono text-white">{current.y_position ?? 'auto'}px</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('y_position', -2, 0, 2000)}
                >
                  ↑ -2
                </button>
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('y_position', 2, 0, 2000)}
                >
                  +2 ↓
                </button>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Width</span>
                <span className="font-mono text-white">{current.width ?? 'auto'}px</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('width', -10, 20, 4000)}
                >
                  -10
                </button>
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('width', 10, 20, 4000)}
                >
                  +10
                </button>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Height</span>
                <span className="font-mono text-white">{current.height ?? 'auto'}px</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('height', -5, 10, 2000)}
                >
                  -5
                </button>
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('height', 5, 10, 2000)}
                >
                  +5
                </button>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded p-3 space-y-2 col-span-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Font Size</span>
                <span className="font-mono text-white">{current.font_size ?? 'default'}px</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('font_size', -1, 6, 72)}
                >
                  -1
                </button>
                <button
                  type="button"
                  className="flex-1 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  onClick={() => adjustNumericField('font_size', 1, 6, 72)}
                >
                  +1
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-700 mt-2">
            <div className="text-sm text-white">
              These overrides apply <span className="font-semibold text-white">only to this user</span> and are
              merged on top of the main certificate template.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetOverridesForUser}
                disabled={saving}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm text-white rounded disabled:opacity-50"
              >
                Reset to Default Layout
              </button>
              <button
                type="button"
                onClick={saveOverrides}
                disabled={saving}
                className="px-4 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-sm text-black font-semibold rounded disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Layout'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Name & Address Text Editor Section */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mt-4">
        <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          📝 Edit Name & Address
          <span className="text-sm font-normal text-white">(Displayed on certificate)</span>
        </h4>
        <p className="text-sm text-white mb-4">
          Override the name and address text that appears on the certificate. Changes apply only to this user's certificates.
        </p>

        <div className="space-y-3">
          {/* Name Fields */}
          <div className="pb-3 border-b border-gray-700">
            <label className="block text-sm font-semibold text-yellow-400 mb-2">👤 Name Override</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  placeholder="Last name"
                />
              </div>
            </div>
          </div>

          {/* Address Fields */}
          <div>
            <label className="block text-sm font-semibold text-yellow-400 mb-2">📍 Address Override</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Address Line 1</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
              placeholder="Street address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Address Line 2</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
              placeholder="City, Country, Postal Code"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                placeholder="City"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                placeholder="Country"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Postal Code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                placeholder="Postal Code"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-700">
            {addressStatus && (
              <span className={`text-sm ${addressStatus.startsWith('✅') ? 'text-green-400' : 'text-red-400'}`}>
                {addressStatus}
              </span>
            )}
            <div className="ml-auto">
              <button
                type="button"
                onClick={saveAddressText}
                disabled={savingAddress}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded disabled:opacity-50"
              >
                {savingAddress ? 'Saving...' : '💾 Save Name & Address'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {statusMessage && (
        <div className="text-sm mt-1">
          {statusMessage.startsWith('✅') ? (
            <span className="text-green-400">{statusMessage}</span>
          ) : (
            <span className="text-red-400">{statusMessage}</span>
          )}
        </div>
      )}
    </div>
  );
};

